import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, FileText, Globe, Loader2, MapPin, RefreshCw, Save, Trash2, Upload, X } from "lucide-react";

const STATUS_LABELS: Record<number, string> = {
  1: "Draft", 2: "Under Review", 3: "Active", 4: "Under Offer",
  5: "Sold", 6: "Expired", 7: "Withdrawn", 8: "Rejected",
  9: "LOI", 10: "Due Diligence", 11: "Escrow",
};

// Pipeline display order: draft → review → active → loi → under offer → due diligence → escrow → sold → expired → withdrawn → rejected
const STATUS_DISPLAY_ORDER: number[] = [1, 2, 3, 9, 4, 10, 11, 5, 6, 7, 8];
const STATUS_COLORS: Record<number, string> = {
  1: "bg-slate-100 text-slate-700",
  2: "bg-amber-100 text-amber-700",
  3: "bg-emerald-100 text-emerald-700",
  4: "bg-cyan-100 text-cyan-700",
  5: "bg-sky-100 text-sky-700",
  6: "bg-zinc-100 text-zinc-700",
  7: "bg-orange-100 text-orange-700",
  8: "bg-rose-100 text-rose-700",
  9: "bg-purple-100 text-purple-700",
  10: "bg-indigo-100 text-indigo-700",
  11: "bg-teal-100 text-teal-700",
};

import { useAuth } from "@/contexts/AuthContext";
import { ADMIN_ROLES } from "@/lib/roles";
import { useToast } from "@/components/ui/toast";
import { api, assetUrl } from "@/lib/api";
import { emailIssue, nameIssue, phoneIssue } from "@/lib/profileValidation";
import { cn } from "@/lib/utils";
import type { BusinessListing } from "@/types";
import Loader from "@/components/ui/loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";

// Warm brand charcoal for the always-dark listing preview card (keeps white
// text + gold price legible in both themes — mirrors the dark showcase look).
const NAVY  = "#1c1710";
const NAVY2 = "#3a2c12";
const GOLD  = "#E8A020";
const GREEN = "#16A34A";
const RED   = "#CC3333";

const selectCls =
  "h-10 w-full rounded-xl border border-border/70 bg-white/80 px-3 py-2 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary/50 disabled:opacity-50 disabled:cursor-not-allowed";

const fmtCompact = (v: string | number | null | undefined): string => {
  const n = Number(v);
  if (!n) return "";
  if (n >= 1_000_000_000) return `$${+(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000)     return `$${+(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)         return `$${+(n / 1_000).toFixed(1)}K`;
  return `$${n}`;
};

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const parsedAskingPrice = (val: string): number | null => {
  if (!val.trim()) return null;
  const n = Number(val.replace(/[$,\s]/g, ""));
  return !isNaN(n) && val.replace(/[$,\s]/g, "") !== "" ? n : null;
};
const isMakeOfferText = (val: string): boolean =>
  !!val.trim() && parsedAskingPrice(val) === null;

export default function EditListingPage() {
  const { id } = useParams<{ id: string }>();
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  // TKT-00127: the page that sent us here (e.g. the advisor's filtered listing
  // table, page and all). Saving returns there instead of stranding the user on
  // their role home, and it is handed on to the listing detail on the fallback
  // path so "Back to listings" still lands on the right page. Absent for entry
  // points that pass no state — those keep the original behaviour.
  const backFrom = (useLocation().state as { from?: string } | null)?.from;
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);

  const isAdmin        = hasRole(...ADMIN_ROLES);
  const isAdminOrAgent = !!user && (hasRole(...ADMIN_ROLES) || hasRole("bl_advisor"));
  const isAgent        = !!user && hasRole("bl_advisor") && !isAdmin;
  const isSeller       = !!user && hasRole("seller") && !isAdminOrAgent;

  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const locationInitializedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageGenerating, setImageGenerating] = useState(false);
  const [imageOptions, setImageOptions] = useState<string[]>([]);

  const [sellers, setSellers] = useState<{ id: string; first_name: string; last_name: string; email: string; phone: string | null }[]>([]);
  const [selectedSellerId, setSelectedSellerId] = useState("");

  const [title, setTitle] = useState("");
  const [overview, setOverview] = useState("");
  const [displayOverview, setDisplayOverview] = useState("");
  const [askingPrice, setAskingPrice] = useState("");
  const [revenue, setRevenue] = useState("");
  const [ebitda, setEbitda] = useState("");
  const [addressLine, setAddressLine] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [stateId, setStateId] = useState("");
  const [cityId, setCityId] = useState("");
  const [cityName, setCityName] = useState("");
  const [countryId, setCountryId] = useState("");
  const [industryCategoryId, setIndustryCategoryId] = useState("");
  const [categories, setCategories] = useState<{ id: number; name: string; is_active?: boolean }[]>([]);
  // The category the listing was saved with. If the super admin has since
  // retired it, it stays selectable here (and only here) so the form neither
  // blanks the field nor forces a re-file on every edit.
  const [savedCategoryId, setSavedCategoryId] = useState<number | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [establishedYear, setEstablishedYear] = useState("");
  const [establishedMonth, setEstablishedMonth] = useState("");
  const [isSbaApproved, setIsSbaApproved] = useState(false);
  const [isSellerFinancing, setIsSellerFinancing] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [sellerName, setSellerName] = useState("");
  const [sellerEmail, setSellerEmail] = useState("");
  const [sellerPhone, setSellerPhone] = useState("");
  const [statusId, setStatusId] = useState("");
  const [statuses, setStatuses] = useState<{ id: number; name: string }[]>([]);

  const [countries, setCountries] = useState<{ id: number; name: string }[]>([]);
  const [states, setStates] = useState<{ id: number; name: string }[]>([]);
  const [cities, setCities] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    if (isAdminOrAgent) api.get<typeof sellers>("/business-listings/sellers").then(setSellers).catch(() => {});
    api.get<{ id: number; name: string }[]>("/lookups/listing-statuses").then(setStatuses).catch(() => {});
    api.get<{ id: number; name: string; is_active?: boolean }[]>("/lookups/categories?include_inactive=true").then(setCategories).catch(() => {});
    api.get<{ id: number; name: string }[]>("/lookups/countries").then(setCountries).catch(() => {});
  }, [isAdminOrAgent]);

  useEffect(() => {
    if (!locationInitializedRef.current) return;
    if (countryId) api.get<{ id: number; name: string }[]>(`/lookups/states?country_id=${countryId}`).then(setStates).catch(() => {});
    else setStates([]);
    setStateId(""); setCityId(""); setCities([]);
  }, [countryId]);

  useEffect(() => {
    if (!locationInitializedRef.current) return;
    if (stateId) api.get<{ id: number; name: string }[]>(`/lookups/cities?state_id=${stateId}`).then(setCities).catch(() => {});
    else setCities([]);
    setCityId("");
    setCityName("");
  }, [stateId]);

  const handleSellerSelect = (sellerId: string) => {
    setSelectedSellerId(sellerId);
    if (!sellerId) { setSellerName(""); setSellerEmail(""); setSellerPhone(""); return; }
    const s = sellers.find((s) => s.id === sellerId);
    if (s) { setSellerName(`${s.first_name} ${s.last_name}`.trim()); setSellerEmail(s.email); setSellerPhone(s.phone || ""); }
  };

  useEffect(() => {
    if (!id) return;
    api.get<BusinessListing>(`/business-listings/${id}`)
      .then(async (l) => {
        if (!isAdminOrAgent && l.listing_status_id !== 1) {
          toast.error("Only draft listings can be edited.");
          navigate(`/listings/${id}`, backFrom ? { state: { from: backFrom } } : undefined);
          return;
        }
        setTitle(l.title);
        setOverview(l.overview || "");
        setDisplayOverview(l.display_overview || "");
        setAskingPrice(l.make_offer ? "Make Offer" : (l.asking_price != null ? String(l.asking_price) : ""));
        setRevenue(l.revenue != null ? String(l.revenue) : "");
        setEbitda(l.ebitda != null ? String(l.ebitda) : "");
        setAddressLine(l.address_line || "");
        setZipCode(l.zip_code || "");
        setIndustryCategoryId(l.industry_category_id != null ? String(l.industry_category_id) : "");
        setSavedCategoryId(l.industry_category_id ?? null);
        setWebsiteUrl(l.website_url || "");
        setEstablishedYear(l.established_year != null ? String(l.established_year) : "");
        setEstablishedMonth(l.established_month != null ? String(l.established_month) : "");
        setIsSbaApproved(l.is_sba_approved);
        setIsSellerFinancing(l.seller_financing ?? false);
        setAdditionalNotes(l.additional_notes || "");
        setSellerName(l.seller_name || "");
        setSellerEmail(l.seller_email || "");
        setSellerPhone(l.seller_phone || "");
        setSelectedSellerId(l.seller_id || "");
        setStatusId(l.listing_status_id != null ? String(l.listing_status_id) : "");
        setExistingImageUrl(l.listing_image_url || null);
        setCountryId(l.country_id != null ? String(l.country_id) : "");

        if (l.country_id) {
          const loadedStates = await api.get<{ id: number; name: string }[]>(`/lookups/states?country_id=${l.country_id}`).catch(() => []);
          setStates(loadedStates);
        }
        if (l.state_id) {
          setStateId(String(l.state_id));
          const loadedCities = await api.get<{ id: number; name: string }[]>(`/lookups/cities?state_id=${l.state_id}`).catch(() => []);
          setCities(loadedCities);
        }
        if (l.city_id) setCityId(String(l.city_id));
        if (!l.city_id && l.city_name) setCityName(l.city_name);
        locationInitializedRef.current = true;
      })
      .catch((err: Error) => {
        if (err.message?.includes("404")) setNotFound(true);
        else toast.error(err.message || "Failed to load listing");
      })
      .finally(() => setPageLoading(false));
  }, [id, isAdmin, navigate, toast]);

  const generateOptions = async () => {
    const cat = categories.find((c) => c.id === Number(industryCategoryId));
    if (!cat) return;
    setImageGenerating(true);
    setImageOptions([]);
    try {
      // Must go through `api`: the endpoint is authenticated, and a bare fetch()
      // sends no bearer token — every generation 401'd, so no options ever came
      // back. `api` also surfaces the server's own detail (e.g. generation not
      // configured) instead of one catch-all message.
      const data = await api.get<{ images: string[] }>(
        `/image-proxy/listing-image-options?q=${encodeURIComponent(cat.name)}&count=2`,
      );
      setImageOptions(data.images);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Image generation failed. Try again.");
    } finally {
      setImageGenerating(false);
    }
  };

  const handleSelectImage = async (dataUrl: string) => {
    if (!id) return;
    setImageUploading(true);
    try {
      const updated = await api.post<BusinessListing>(`/business-listings/${id}/image-base64`, { image_data: dataUrl });
      setExistingImageUrl(updated.listing_image_url || null);
      setCoverImagePreview(dataUrl);
      toast.success("Cover image saved");
    } catch {
      toast.error("Failed to save image");
    } finally {
      setImageUploading(false);
    }
  };

  const handleImageDelete = async () => {
    if (!id) return;
    setImageUploading(true);
    try {
      await api.delete(`/business-listings/${id}/image`);
      setExistingImageUrl(null);
      setCoverImagePreview(null);
      toast.success("Cover image removed");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to remove image");
    } finally {
      setImageUploading(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!id) return;
    if (!file.type.startsWith("image/")) { toast.error("Only image files are allowed."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image too large. Maximum 10 MB."); return; }
    setImageUploading(true);
    try {
      const updated = await api.upload<BusinessListing>(`/business-listings/${id}/image`, file);
      setExistingImageUrl(updated.listing_image_url || null);
      setCoverImagePreview(null);
      toast.success("Cover image uploaded");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to upload image");
    } finally {
      setImageUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Seller-details validation — sellers edit their own listing with these fields
  // locked to their profile, so only hand-typed values are checked.
  const sellerNameError = !isSeller ? nameIssue(sellerName, "Seller name") : null;
  const sellerEmailError = !isSeller ? emailIssue(sellerEmail, "Seller email") : null;
  const sellerPhoneError = !isSeller ? phoneIssue(sellerPhone) : null;

  const handleSubmit = async (overrideStatusId?: number) => {
    if (!id || !formRef.current?.reportValidity()) return;
    if (!industryCategoryId) {
      toast.error("Select an industry category.");
      return;
    }
    if (!countryId) {
      toast.error("Select a country.");
      return;
    }
    if (!stateId) {
      toast.error("Select a state.");
      return;
    }
    const sellerIssue = sellerNameError ?? sellerEmailError ?? sellerPhoneError;
    if (sellerIssue) {
      toast.error(sellerIssue);
      return;
    }
    setSaving(true);
    const body: Record<string, unknown> = {
      title, overview: overview || null, display_overview: displayOverview || null,
      asking_price: parsedAskingPrice(askingPrice),
      make_offer: isMakeOfferText(askingPrice),
      revenue: revenue ? Number(revenue) : null,
      ebitda: ebitda ? Number(ebitda) : null,
      address_line: addressLine || null, zip_code: zipCode || null,
      state_id: stateId ? Number(stateId) : null,
      city_id: cityId ? Number(cityId) : null,
      city_name: !cityId && cityName ? cityName : null,
      country_id: countryId ? Number(countryId) : null,
      industry_category_id: industryCategoryId ? Number(industryCategoryId) : null,
      website_url: websiteUrl || null,
      established_year: establishedYear ? Number(establishedYear) : null,
      established_month: establishedMonth ? Number(establishedMonth) : null,
      is_sba_approved: isSbaApproved, seller_financing: isSellerFinancing,
      additional_notes: additionalNotes || null,
      seller_name: sellerName.trim() || null, seller_email: sellerEmail.trim() || null,
      seller_phone: sellerPhone.trim() || null, seller_id: selectedSellerId || null,
    };
    if (overrideStatusId !== undefined) {
      body.listing_status_id = overrideStatusId;
    } else if (isAdmin && statusId) {
      body.listing_status_id = Number(statusId);
    }
    try {
      await api.patch<BusinessListing>(`/business-listings/${id}`, body);
      if (isAgent) {
        if (Number(statusId) === 3) {
          toast.success("Listing updated.");
        } else {
          toast.success("Saved. Submitted to admin for approval.");
        }
      } else {
        toast.success("Listing updated.");
      }
      // TKT-00127: back to the exact list the edit was launched from, so an
      // advisor working through several listings keeps their place.
      navigate(backFrom ?? `/listings/${id}`);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to update listing");
    } finally {
      setSaving(false);
    }
  };

  // Loading screen
  if (pageLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader label="Loading listing…" />
      </div>
    );
  }

  if (notFound || !user) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-4">
        <div className="rounded-2xl border border-border/60 bg-white p-8 text-center shadow-sm">
          <p className="text-sm font-semibold text-muted-foreground">{notFound ? "Listing not found." : "Sign in to edit listings."}</p>
          <Button className="mt-4 rounded-full" onClick={() => navigate(-1)}>Go back</Button>
        </div>
      </div>
    );
  }

  // ── Derived preview values ──────────────────────────────────────────
  const previewCategory  = categories.find((c) => c.id === Number(industryCategoryId));
  const previewCountry   = countries.find((c) => c.id === Number(countryId));
  const previewState     = states.find((s) => s.id === Number(stateId));
  const previewCity      = cities.find((c) => c.id === Number(cityId));
  const locationParts    = [previewCity?.name || cityName, previewState?.name, previewCountry?.name].filter(Boolean);
  const estLabel = establishedYear
    ? `Est. ${establishedMonth ? MONTHS[Number(establishedMonth) - 1] + " " : ""}${establishedYear}`
    : null;
  const _parsedPrice = parsedAskingPrice(askingPrice);
  const _isMakeOffer = isMakeOfferText(askingPrice);
  const cfMultiple = (_parsedPrice && ebitda && Number(ebitda) > 0)
    ? `${(_parsedPrice / Number(ebitda)).toFixed(2)}x`
    : null;
  const metrics = [
    revenue     && { label: "Annual Revenue", value: fmtCompact(revenue),                                           color: GOLD  },
    ebitda      && { label: "Adj. EBITDA",    value: fmtCompact(ebitda),                                            color: GREEN },
    cfMultiple  && { label: "C.F. Multiple",  value: cfMultiple,                                                    color: GREEN },
    askingPrice && { label: "Asking Price",   value: _isMakeOffer ? askingPrice : fmtCompact(String(_parsedPrice)), color: RED   },
  ].filter(Boolean) as { label: string; value: string; color: string }[];

  const currentStatusName = statuses.find((s) => String(s.id) === statusId)?.name || "draft";

  return (
    /* Regular interior page (was a full-screen modal overlay): scrolls with
       the whole page like every other shell page; the live preview sticks
       beside the form while scrolling. */
    <div className="w-full">
      <div className="w-full space-y-5 px-4 py-6 sm:px-6">

        {/* ── Header — same Dark · Gold hero as the other interior pages ── */}
        <section className="public-hero-shell rounded-[1.85rem] px-6 py-7 shadow-glow sm:px-8">
          <div className="public-grid-lattice absolute inset-0 opacity-45" />
          <div className="absolute -right-14 top-0 h-40 w-40 rounded-full bg-white/30 blur-3xl" />
          <div className="relative z-10 flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="public-pill">
                <FileText className="h-3.5 w-3.5" />
                Listing Builder
              </div>
              <h1 className="mt-4 font-display text-3xl leading-tight text-foreground sm:text-4xl">
                Edit <span className="text-gradient">Listing</span>
              </h1>
              <p className="mt-2 max-w-2xl truncate text-sm leading-7 text-muted-foreground sm:text-base">{title || "Loading…"}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={cn(
                "rounded-full px-3 py-1 text-[0.62rem] font-bold uppercase tracking-wide",
                STATUS_COLORS[Number(statusId)] || "bg-slate-100 text-slate-700"
              )}>
                {STATUS_LABELS[Number(statusId)] || currentStatusName.replace(/_/g, " ")}
              </span>
              <button onClick={() => navigate(-1)} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted transition-colors">
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          </div>
        </section>

        {/* ── Body ── */}
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start">

          {/* LEFT: form */}
          <div className="min-w-0 flex-1">
            <form ref={formRef} noValidate className="space-y-4">

              {/* Admin: status picker */}
              {isAdmin && statuses.length > 0 && (
                <section className="rounded-2xl border border-border/60 bg-white/85 p-5">
                  <p className="mb-3 text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Listing Status</p>
                  <div className="flex flex-wrap gap-2">
                    {[...statuses].sort((a, b) => {
                      const ai = STATUS_DISPLAY_ORDER.indexOf(a.id);
                      const bi = STATUS_DISPLAY_ORDER.indexOf(b.id);
                      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                    }).map((s) => (
                      <button key={s.id} type="button" onClick={() => setStatusId(String(s.id))}
                        className={cn(
                          "rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors",
                          String(s.id) === statusId
                            ? "border-primary bg-gold-gradient text-primary-foreground shadow-sm"
                            : "border-border/60 bg-white text-muted-foreground hover:border-primary/40 hover:text-foreground"
                        )}>
                        {STATUS_LABELS[s.id] ?? s.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {/* Business profile */}
              <section className="rounded-2xl border border-border/60 bg-white/85 p-5">
                <p className="mb-4 text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Business Profile</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Business title *</Label>
                    <Input value={title} onChange={(e) => setTitle(e.target.value)} required className="h-10 rounded-xl" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Industry category *</Label>
                    <SearchableSelect
                      value={industryCategoryId}
                      onChange={setIndustryCategoryId}
                      options={categories
                        .filter((c) => c.is_active !== false || c.id === savedCategoryId)
                        .map((c) => ({ value: String(c.id), label: c.is_active === false ? `${c.name} (retired)` : c.name }))}
                      placeholder="Select category"
                      className="h-10 rounded-xl border-border/70 bg-white/80"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Website URL</Label>
                    <Input type="url" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} className="h-10 rounded-xl" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Established year</Label>
                    <Input type="number" min="1800" max="2100" value={establishedYear} onChange={(e) => setEstablishedYear(e.target.value)} className="h-10 rounded-xl" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Established month</Label>
                    <Input type="number" min="1" max="12" value={establishedMonth} onChange={(e) => setEstablishedMonth(e.target.value)} className="h-10 rounded-xl" />
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-amber-700">
                    <input type="checkbox" checked={isSbaApproved} onChange={(e) => setIsSbaApproved(e.target.checked)} className="h-4 w-4 rounded accent-primary" />
                    SBA Pre-Approved
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-green-100 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-green-700">
                    <input type="checkbox" checked={isSellerFinancing} onChange={(e) => setIsSellerFinancing(e.target.checked)} className="h-4 w-4 rounded accent-primary" />
                    Seller Financing
                  </label>
                </div>
              </section>

              {/* Overview */}
              <section className="rounded-2xl border border-border/60 bg-white/85 p-5">
                <p className="mb-4 text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Overview &amp; Description</p>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Business description</Label>
                    <Textarea rows={8} value={overview} onChange={(e) => setOverview(e.target.value)} className="rounded-xl resize-none text-sm" />
                  </div>
                  {isAdminOrAgent && (
                    <div className="space-y-1.5">
                      <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Public display description *</Label>
                      <Textarea rows={6} value={displayOverview} onChange={(e) => setDisplayOverview(e.target.value)} required className="rounded-xl resize-none text-sm" />
                    </div>
                  )}
                </div>
              </section>

              {/* Financials */}
              <section className="rounded-2xl border border-border/60 bg-white/85 p-5">
                <p className="mb-4 text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Financial Metrics</p>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Asking price (USD)</Label>
                    <Input
                      type="text"
                      placeholder="e.g. 1500000 or 'Make Offer'"
                      value={askingPrice}
                      onChange={(e) => setAskingPrice(e.target.value)}
                      className="h-10 rounded-xl"
                    />
                    <p className="text-[0.65rem] text-muted-foreground/60">Enter a dollar amount or text like "Make Offer" / "Best Offer"</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Annual revenue (USD) *</Label>
                    <Input type="number" min="0" step="0.01" value={revenue} onChange={(e) => setRevenue(e.target.value)} required className="h-10 rounded-xl" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">EBITDA (USD) *</Label>
                    <Input type="number" min="0" step="0.01" value={ebitda} onChange={(e) => setEbitda(e.target.value)} required className="h-10 rounded-xl" />
                  </div>
                </div>
              </section>

              {/* Location */}
              <section className="rounded-2xl border border-border/60 bg-white/85 p-5">
                <p className="mb-4 text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Location</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Street address</Label>
                    <Input value={addressLine} onChange={(e) => setAddressLine(e.target.value)} className="h-10 rounded-xl" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Zip code</Label>
                    <Input value={zipCode} onChange={(e) => setZipCode(e.target.value)} className="h-10 rounded-xl" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Country *</Label>
                    <SearchableSelect
                      value={countryId}
                      onChange={setCountryId}
                      options={countries.map((c) => ({ value: String(c.id), label: c.name }))}
                      placeholder="Select country"
                      className="h-10 rounded-xl border-border/70 bg-white/80"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">State *</Label>
                    <SearchableSelect
                      value={stateId}
                      onChange={setStateId}
                      options={states.map((s) => ({ value: String(s.id), label: s.name }))}
                      placeholder="Select state"
                      className="h-10 rounded-xl border-border/70 bg-white/80"
                      disabled={!countryId}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">City</Label>
                    <SearchableSelect
                      value={cityId}
                      onChange={(v) => { setCityId(v); if (v) setCityName(""); }}
                      options={cities.map((c) => ({ value: String(c.id), label: c.name }))}
                      placeholder="Select city"
                      className="h-10 rounded-xl border-border/70 bg-white/80"
                      disabled={!stateId || !!cityName}
                    />
                    {stateId && (
                      <Input
                        placeholder="City not in list? Type it here"
                        value={cityName}
                        onChange={(e) => { setCityName(e.target.value); if (e.target.value) setCityId(""); }}
                        className="h-9 rounded-xl text-sm"
                      />
                    )}
                  </div>
                </div>
              </section>

              {/* Seller details */}
              <section className="rounded-2xl border border-border/60 bg-white/85 p-5">
                <p className="mb-4 text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Seller Details</p>
                {isAdminOrAgent && (
                  <div className="mb-4 space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Select registered seller</Label>
                    <select value={selectedSellerId} onChange={(e) => handleSellerSelect(e.target.value)} className={selectCls}>
                      <option value="">Choose a seller or enter manually</option>
                      {sellers.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.email})</option>)}
                    </select>
                  </div>
                )}
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Seller name *</Label>
                    <Input value={sellerName} onChange={(e) => setSellerName(e.target.value)} required maxLength={60} disabled={isSeller} className="h-10 rounded-xl" />
                    {sellerName.trim() !== "" && sellerNameError && (
                      <p className="text-xs font-medium text-destructive">{sellerNameError}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Seller email *</Label>
                    <Input type="email" value={sellerEmail} onChange={(e) => setSellerEmail(e.target.value)} required disabled={isSeller} className="h-10 rounded-xl" />
                    {sellerEmail.trim() !== "" && sellerEmailError && (
                      <p className="text-xs font-medium text-destructive">{sellerEmailError}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Seller phone</Label>
                    <Input type="tel" value={sellerPhone} onChange={(e) => setSellerPhone(e.target.value)} maxLength={20} disabled={isSeller} className="h-10 rounded-xl" />
                    {sellerPhoneError && (
                      <p className="text-xs font-medium text-destructive">{sellerPhoneError}</p>
                    )}
                  </div>
                </div>
              </section>

              {/* Cover Image */}
              <section className="rounded-2xl border border-border/60 bg-white/85 p-5">
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Cover Image</p>
                  {(coverImagePreview || existingImageUrl) && (
                    <button type="button" disabled={imageUploading || imageGenerating} onClick={handleImageDelete}
                      className="flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-[0.65rem] text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50">
                      <Trash2 className="h-3 w-3" /> Remove
                    </button>
                  )}
                </div>

                {imageGenerating ? (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border/50 bg-gray-50 py-10">
                    <Loader2 className="h-8 w-8 animate-spin text-primary/50" />
                    <span className="text-xs text-muted-foreground">Generating 2 options with AI…</span>
                    <span className="text-[0.65rem] text-muted-foreground/50">This may take ~30s</span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Current saved / selected image */}
                    {(coverImagePreview || existingImageUrl) && (
                      <div className="relative overflow-hidden rounded-xl border-2 border-primary">
                        <img src={coverImagePreview || (assetUrl(existingImageUrl) ?? "")} alt="Cover" className="h-36 w-full object-cover" />
                        {imageUploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <Loader2 className="h-6 w-6 animate-spin text-white" />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Options grid */}
                    {imageOptions.length > 0 && (
                      <div className="grid grid-cols-2 gap-2">
                        {imageOptions.map((url, i) => {
                          const isSelected = coverImagePreview === url;
                          return (
                            <div key={i} className={`relative ${imageUploading ? "pointer-events-none opacity-50" : "cursor-pointer"}`} onClick={() => !imageUploading && handleSelectImage(url)}>
                              <div className={`overflow-hidden rounded-xl border-2 transition-all ${isSelected ? "border-primary ring-2 ring-primary/20" : "border-transparent hover:border-primary/40"}`}>
                                <img src={url} alt={`Option ${i + 1}`} className="h-32 w-full object-cover" />
                              </div>
                              <div className="absolute top-2 left-2 flex items-center justify-center">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled={imageUploading}
                                  onChange={() => handleSelectImage(url)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-5 w-5 cursor-pointer rounded accent-primary shadow-md"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Generate / Regenerate button — AI needs a category to
                        prompt with, but uploading your own never did, so this is
                        disabled on its own rather than hiding the whole block. */}
                    <button type="button" onClick={generateOptions} disabled={imageGenerating || imageUploading || !industryCategoryId}
                      className="flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-border/50 bg-gray-50 py-3 text-xs text-muted-foreground transition-colors hover:bg-gray-100 disabled:opacity-50">
                      {imageOptions.length > 0 ? (
                        <><RefreshCw className="h-3.5 w-3.5" /> Regenerate options</>
                      ) : (
                        <><span>🎨</span> Generate cover images with AI</>
                      )}
                    </button>
                    {!industryCategoryId && (
                      <p className="text-center text-[0.65rem] text-muted-foreground/60">Select a category first to generate cover images with AI</p>
                    )}

                    {/* Divider */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 border-t border-border/40" />
                      <span className="text-[0.65rem] font-medium text-muted-foreground/50 uppercase tracking-widest">or</span>
                      <div className="flex-1 border-t border-border/40" />
                    </div>

                    {/* Upload button */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file);
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={imageUploading || imageGenerating}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border/50 bg-gray-50 py-3 text-xs text-muted-foreground transition-colors hover:bg-gray-100 hover:border-primary/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      Upload your own photo (JPG, PNG, WEBP · max 10 MB)
                    </button>
                  </div>
                )}
              </section>

              {/* Additional notes */}
              <section className="rounded-2xl border border-border/60 bg-white/85 p-5">
                <p className="mb-4 text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Additional Notes</p>
                <Textarea rows={3} value={additionalNotes} onChange={(e) => setAdditionalNotes(e.target.value)} className="rounded-xl resize-none text-sm" />
              </section>
            </form>
          </div>

          {/* RIGHT: live preview — full natural height, scrolls with the page */}
          <div className="hidden w-[400px] shrink-0 lg:block">
            <div className="mb-3 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-muted-foreground">Live Preview — what buyers will see</span>
            </div>

            <div className="overflow-hidden rounded-2xl border border-border/50 bg-white shadow-md">
              {/* Cover image */}
              {(coverImagePreview || existingImageUrl) && (
                <img
                  src={coverImagePreview || (assetUrl(existingImageUrl) ?? "")}
                  alt="Cover"
                  className="h-32 w-full object-cover"
                />
              )}
              {/* Navy header */}
              <div className="px-5 py-4 text-white" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY2} 100%)` }}>
                {previewCategory && (
                  <span className="mb-2 inline-block rounded-full bg-white/20 px-2.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-widest text-white/90">
                    {previewCategory.name}
                  </span>
                )}
                <h2 className="text-lg font-extrabold leading-tight text-white">
                  {title || <span className="italic text-white/40">Business title…</span>}
                </h2>
                {locationParts.length > 0 && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-xs text-white/70">
                    <MapPin className="h-3 w-3 shrink-0" />
                    {locationParts.join(", ")}
                  </div>
                )}
                {estLabel && <div className="mt-1 text-xs text-white/60">{estLabel}</div>}
                {(isSbaApproved || isSellerFinancing) && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {isSbaApproved && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/25 px-2 py-0.5 text-[0.6rem] font-bold text-emerald-400">
                        <CheckCircle2 className="h-3 w-3" /> SBA Pre-Approved
                      </span>
                    )}
                    {isSellerFinancing && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-400/25 px-2 py-0.5 text-[0.6rem] font-bold text-blue-400">
                        <CheckCircle2 className="h-3 w-3" /> Seller Financing
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Metrics bar */}
              {metrics.length > 0 && (
                <div className="grid divide-x divide-border/50 border-b border-border/50"
                  style={{ gridTemplateColumns: `repeat(${metrics.length}, 1fr)` }}>
                  {metrics.map((m) => (
                    <div key={m.label} className="flex flex-col items-center py-3 px-2">
                      <span className="text-base font-extrabold" style={{ color: m.color }}>{m.value}</span>
                      <span className="mt-0.5 text-[0.55rem] font-bold uppercase tracking-widest text-muted-foreground">{m.label}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="p-4">
                {(overview || displayOverview) ? (
                  <>
                    <div className="mb-1 flex items-center gap-1.5">
                      <div className="h-3.5 w-0.5 rounded-full bg-primary" />
                      <span className="text-[0.6rem] font-bold uppercase tracking-widest text-muted-foreground">The Opportunity</span>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-gray-700 line-clamp-4">{overview || displayOverview}</p>
                  </>
                ) : (
                  <p className="text-xs italic text-muted-foreground/50">Business description will appear here…</p>
                )}

                {askingPrice && (
                  <div className="mt-4 overflow-hidden rounded-xl shadow" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY2} 100%)` }}>
                    <div className="p-4 text-center">
                      <p className="text-[0.55rem] font-bold uppercase tracking-widest text-white/50 mb-1">Asking Price</p>
                      <p className="text-2xl font-extrabold" style={{ color: GOLD }}>{_isMakeOffer ? askingPrice : fmtCompact(String(_parsedPrice))}</p>
                      {cfMultiple && <p className="mt-0.5 text-xs text-white/60">{cfMultiple} Cash Flow Multiple</p>}
                    </div>
                    {(revenue || ebitda) && (
                      <div className="grid grid-cols-2 divide-x divide-white/10 border-t border-white/10">
                        {revenue && (
                          <div className="py-3 text-center">
                            <p className="text-[0.55rem] font-bold uppercase tracking-widest text-white/50">Revenue</p>
                            <p className="mt-0.5 text-sm font-extrabold" style={{ color: GOLD }}>{fmtCompact(revenue)}</p>
                          </div>
                        )}
                        {ebitda && (
                          <div className="py-3 text-center">
                            <p className="text-[0.55rem] font-bold uppercase tracking-widest text-white/50">EBITDA</p>
                            <p className="mt-0.5 text-sm font-extrabold" style={{ color: "#86efac" }}>{fmtCompact(ebitda)}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {(websiteUrl || additionalNotes) && (
                  <div className="mt-4 space-y-2 rounded-xl border border-border/50 bg-gray-50 p-3">
                    <p className="text-[0.6rem] font-bold uppercase tracking-widest text-muted-foreground">Details</p>
                    {websiteUrl && (
                      <div className="flex items-center gap-1.5 text-xs text-primary">
                        <Globe className="h-3 w-3 shrink-0" />
                        <span className="truncate">{websiteUrl}</span>
                      </div>
                    )}
                    {additionalNotes && <p className="text-xs text-gray-600 line-clamp-2">{additionalNotes}</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer actions ── */}
        <div className="flex items-center justify-between rounded-2xl border border-border/60 bg-card px-5 py-4 shadow-sm">
          <p className="text-xs text-muted-foreground">Changes are saved immediately to this listing record.</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(-1)} disabled={saving} className="rounded-xl">
              Cancel
            </Button>
            {isAgent ? (
              statusId === "3" ? (
                <Button onClick={() => handleSubmit()} disabled={saving} className="rounded-xl">
                  <Save className="h-4 w-4" />
                  {saving ? "Saving…" : "Update"}
                </Button>
              ) : (
                <Button onClick={() => handleSubmit()} disabled={saving} className="rounded-xl bg-amber-600 text-white hover:bg-amber-700">
                  <RefreshCw className="h-4 w-4" />
                  {saving ? "Submitting…" : "Save & Submit for Review"}
                </Button>
              )
            ) : (
              <>
                <Button onClick={() => handleSubmit()} disabled={saving} className="rounded-xl">
                  <Save className="h-4 w-4" />
                  {saving ? "Saving…" : statusId === "3" ? "Update" : "Save Changes"}
                </Button>
                {statusId === "1" && (
                  <Button onClick={() => handleSubmit(isAdmin ? 3 : 2)} disabled={saving} className="rounded-xl">
                    {saving ? "Submitting…" : isAdmin ? "Submit" : "Submit for Review"}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
